import { Injectable } from '@angular/core';

import { Adapter } from '../adapter';
import {User, UserRoles} from './user.model';

export class ManagementUser implements User {
  userID: number ;
  userRole: string ;
  effectiveUserRole: string ;
  firstName: string ;
  middleName: string | null ;
  lastName: string ;
  username: string ;
  password: string | null ;
  enabled: boolean ;
  externalIdentities: { [key: string] : string } ;
  email: string | null ;
  school: string ;
  schoolID: number ;
  timeZoneId: string | null ;
  numberOfUserLogins: number | null ;
  numberOfActiveStudents: number | null ;
  failedLogins: number ;
  lockedUntil: Date | null ;
  lastFailedAttempt: Date | null ;
  districtID: number ;
  districtName: string | null ;
  diagnosticProductEnabled: boolean ;
  fullProductEnabled: boolean ;
  fullProductSubscriptionAssigned: boolean ;
  territory: string | null ;
  canEnableBetaTasks: boolean | null ;
  demoModeEnabled: boolean = false ;

  constructor(item: any) {
    this.userID = item.userID ;
    this.userRole = item.userRole ;
    this.firstName = item.firstName ;
    this.middleName = item.middleName ;
    this.lastName = item.lastName ;
    this.username = item.username ;
    this.password = item.password ;
    this.enabled = item.enabled ;
    this.externalIdentities = item.externalIdentities ;
    this.school = item.school ;
    this.schoolID = item.schoolID ;
    this.email = item.email ;
    this.timeZoneId = item.timeZoneId ;
    this.numberOfUserLogins = item.numberOfUserLogins ;
    this.numberOfActiveStudents = item.numberOfActiveStudents ;
    this.districtID = item.districtID ;
    this.districtName = item.districtName ;
    this.diagnosticProductEnabled = item.diagnosticProductEnabled ;
    this.fullProductEnabled = item.fullProductEnabled ;
    this.fullProductSubscriptionAssigned = item.fullProductSubscriptionAssigned ;
    this.territory = item.territory ;
    this.canEnableBetaTasks = item.canEnableBetaTasks ;
    this.failedLogins = item.failedLogins ;
    this.lockedUntil = item.lockedUntil ? new Date(item.lockedUntil) : null ;
    this.lastFailedAttempt = item.lastFailedAttempt ? new Date(item.lastFailedAttempt) : null ;

    // The roles 'TEACHER_SCHOOL_REPORTS' and 'TEACHER_SCHOOL_ADMIN' are multi-function roles that essentially combine the abilities
    // of the 'TEACHER' and 'SCHOOL_REPORTS/ADMIN' roles. In the UI, we allow the user to switch between these two roles.
    // We store which role is chosen for that user group in the 'effectiveUserRole' field, so we need to set that
    // properly. When our user object is created, we will default it to the 'TEACHER' role for these users
    const isTeacherRole =
      this.userRole === UserRoles.TEACHER_SCHOOL_REPORTS ||
      this.userRole === UserRoles.TEACHER_SCHOOL_ADMIN;

    const fallbackRole = isTeacherRole ? UserRoles.TEACHER : '';

    this.effectiveUserRole = item.effectiveUserRole || fallbackRole;

  }

  public isTeacherUser(): boolean {
    return (
      this.userRole === UserRoles.TEACHER ||
      this.userRole === UserRoles.TEACHER_CURRICULUM ||
      this.userRole === UserRoles.TEACHER_REVIEW ||
      this.effectiveUserRole === UserRoles.TEACHER
    ) ;
  }

  public isSchoolUser(): boolean {
    return (this.userRole === UserRoles.SCHOOL_REPORTS || this.effectiveUserRole === UserRoles.SCHOOL_REPORTS) ;
  }

  public isTeacherSchoolReportsUser(): boolean {
    return (this.userRole === UserRoles.TEACHER_SCHOOL_REPORTS) ;
  }

  public isDistrictUser(): boolean {
    return (this.userRole === UserRoles.DISTRICT_REPORTS) ;
  }

  public isSalesRepUser(): boolean {
    return (this.userRole === UserRoles.SALES_REP) ;
  }

  public isTeacherReviewUser(): boolean {
    return (this.userRole === UserRoles.TEACHER_REVIEW) ;
  }

  public isCustomerServiceUser(): boolean {
    return (this.userRole === UserRoles.CUSTOMER_SERVICE) ;
  }

  public isParentUser(): boolean {
    return (this.userRole === UserRoles.PARENT) ;
  }

  public isFILUser(): boolean {
    return (this.userRole === UserRoles.SALES_REP) || (this.userRole.toUpperCase() === UserRoles.FOUNDATIONS_REPORTS) ;
  }

  public isSchoolAdmin(): boolean {
    return (this.userRole === UserRoles.SCHOOL_ADMIN) ;
  }

  public isTeacherSchoolAdmin(): boolean {
    return (this.userRole === UserRoles.TEACHER_SCHOOL_ADMIN) ;
  }

  public isFreeUser(): boolean {
    return (this.districtID === FREE_SCREENER_DISTRICT && this.schoolID === FREE_SCREENER_SCHOOL) ||
      (this.districtID === CLEVER_SCREENER_DISTRICT && this.schoolID === CLEVER_SCREENER_SCHOOL) ;
  }

  // This 'generic' user is used mostly in dropdowns for filters (report filters)
  public static getGenericUser(): ManagementUser {
    return new ManagementUser({
     userID: 0,
     userRole: UserRoles.TEACHER,
     firstName: 'All',
     middleName: '',
     lastName: '',
     username: 'all',
     password: '',
     enabled: true,
     externalIdentities: {},
     school: '',
     schoolID: 0,
     email: '',
     timeZoneId: '',
     numberOfUserLogins: 0,
     districtID: 0,
     territory: '',
     districtName: '',
     diagnosticProductEnabled: false,
     fullProductEnabled: false,
     fullProductSubscriptionAssigned: false,
      failedLogins: 0,
      lockedUntil: null,
      lastFailedAttempt: null,
    }) ;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ManagementUserAdapter implements Adapter<ManagementUser> {
  adapt(item: any): ManagementUser {
    return new ManagementUser(item) ;
  }
}

export type EditableManagementUser = Pick<ManagementUser,
  'userID' |
  'firstName' |
  'lastName' |
  'email' |
  'username' |
  'userRole' |
  'districtID' |
  'schoolID'
> ;

export const FREE_SCREENER_DISTRICT = 182 ;
export const FREE_SCREENER_SCHOOL = 461 ;
export const CLEVER_SCREENER_DISTRICT = 39 ;
export const CLEVER_SCREENER_SCHOOL = 107 ;
